import type { IUserModel } from '@/types/user';

type PendoVisitorData = Pick<IUserModel, 'rolId' | 'companyId'> & {
  id: string;
};

const PENDO_LOG_PREFIX = '[Pendo]';

// Spam prevention configuration
const SPAM_PREVENTION = {
  MIN_INTERVAL_MS: 30000, // Minimum 30 seconds between initializations
  DEBOUNCE_MS: 3000, // 3 seconds debounce for rapid calls
};

// State for spam prevention
let lastInitializationTime = 0;
let lastVisitorHash: string | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let isInitializing = false;

/**
 * Pendo logs with consistent format
 */
const pendoLog = {
  info: (message: string, data?: unknown) => {
    console.log(`${PENDO_LOG_PREFIX} ℹ️ ${message}`, data ?? '');
  },
  success: (message: string, data?: unknown) => {
    console.log(`${PENDO_LOG_PREFIX} ✅ ${message}`, data ?? '');
  },
  warn: (message: string, data?: unknown) => {
    console.warn(`${PENDO_LOG_PREFIX} ⚠️ ${message}`, data ?? '');
  },
  error: (message: string, data?: unknown) => {
    console.error(`${PENDO_LOG_PREFIX} ❌ ${message}`, data ?? '');
  },
};

/**
 * Creates a hash from visitor data to detect changes
 */
const createVisitorHash = (user: PendoVisitorData): string => {
  return `${user.id}-${user.rolId}-${user.companyId}`;
};

/**
 * Validates visitor data before sending to Pendo
 */
const isValidVisitorData = (user: PendoVisitorData): boolean => {
  // Check if id exists and is not empty
  if (!user.id || String(user.id).trim() === '') {
    pendoLog.warn('Invalid visitor data: id is empty');

    return false;
  }

  // Check if id is a valid value (not "0", "undefined", "null", etc.)
  const invalidIds = ['0', 'undefined', 'null', 'NaN', ''];
  if (invalidIds.includes(String(user.id).trim())) {
    pendoLog.warn('Invalid visitor data: id has invalid value', user.id);

    return false;
  }

  // Check if rolId is a valid number
  if (user.rolId === undefined || user.rolId === null || isNaN(user.rolId)) {
    pendoLog.warn('Invalid visitor data: rolId is invalid', user.rolId);

    return false;
  }

  // Check if companyId is a valid number
  if (
    user.companyId === undefined ||
    user.companyId === null ||
    isNaN(user.companyId)
  ) {
    pendoLog.warn('Invalid visitor data: companyId is invalid', user.companyId);

    return false;
  }

  return true;
};

/**
 * Checks if enough time has passed since last initialization
 */
const canInitialize = (): boolean => {
  const now = Date.now();
  const timeSinceLastInit = now - lastInitializationTime;

  if (timeSinceLastInit < SPAM_PREVENTION.MIN_INTERVAL_MS) {
    pendoLog.warn(
      `Rate limited: ${Math.ceil(
        (SPAM_PREVENTION.MIN_INTERVAL_MS - timeSinceLastInit) / 1000,
      )}s until next allowed initialization`,
    );

    return false;
  }

  return true;
};

/**
 * Checks if visitor data has changed since last initialization
 */
const hasVisitorChanged = (user: PendoVisitorData): boolean => {
  const currentHash = createVisitorHash(user);

  if (lastVisitorHash === currentHash) {
    pendoLog.info('Visitor data unchanged, skipping reinitialization');

    return false;
  }

  return true;
};

/**
 * Sets up Pendo event listeners to monitor state
 */
const setupPendoEventListeners = (): void => {
  if (!window.pendo?.events) return;

  window.pendo.events
    .ready(() => {
      pendoLog.success('Pendo is ready and connected');
      logPendoStatus();
    })
    .guidesLoaded(() => {
      pendoLog.info('Pendo guides loaded successfully');
    })
    .guidesFailed(() => {
      pendoLog.error('Failed to load Pendo guides');
    });
};

/**
 * Displays the current Pendo status in the console
 */
export const logPendoStatus = (): void => {
  if (!window.pendo) {
    pendoLog.warn('Pendo is not available on window');

    return;
  }

  const isReady = window.pendo.isReady?.() ?? false;
  const visitorId = window.pendo.getVisitorId?.() ?? 'Not available';
  const accountId = window.pendo.getAccountId?.() ?? 'Not available';
  const version = window.pendo.getVersion?.() ?? 'Not available';
  const currentUrl = window.pendo.getCurrentUrl?.() ?? 'Not available';

  console.group(`${PENDO_LOG_PREFIX} 📊 Pendo Status`);
  console.log('Status:', isReady ? '✅ Connected' : '❌ Disconnected');
  console.log('Visitor ID:', visitorId);
  console.log('Account ID:', accountId);
  console.log('Version:', version);
  console.log('Current URL:', currentUrl);
  console.log('Last visitor hash:', lastVisitorHash);
  console.log(
    'Time since last init:',
    lastInitializationTime
      ? `${Math.floor((Date.now() - lastInitializationTime) / 1000)}s`
      : 'Never',
  );
  console.groupEnd();
};

/**
 * Verifies Pendo connection and displays diagnostic information
 */
export const checkPendoConnection = (): boolean => {
  if (!window.pendo) {
    pendoLog.error('Pendo is not loaded');

    return false;
  }

  const isReady = window.pendo.isReady?.() ?? false;

  if (isReady) {
    pendoLog.success('Pendo connection verified');
    logPendoStatus();
  } else {
    pendoLog.warn('Pendo is loaded but not ready');
  }

  return isReady;
};

/**
 * Resets spam prevention state (useful for testing or forced reinitialization)
 */
export const resetPendoState = (): void => {
  lastInitializationTime = 0;
  lastVisitorHash = null;
  isInitializing = false;
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  pendoLog.info('Pendo state reset');
};

export const loadPendoScript = (user: PendoVisitorData): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Clear any pending debounce
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Debounce rapid calls
    debounceTimer = setTimeout(() => {
      loadPendoScriptInternal(user).then(resolve).catch(reject);
    }, SPAM_PREVENTION.DEBOUNCE_MS);
  });
};

const loadPendoScriptInternal = (user: PendoVisitorData): Promise<void> => {
  return new Promise((resolve, reject) => {
    const apiKey = import.meta.env.VITE_PENDO_API_KEY;

    // Validate API key
    if (!apiKey) {
      pendoLog.warn(
        'VITE_PENDO_API_KEY is not defined. Pendo will not be loaded.',
      );
      resolve();

      return;
    }

    // Validate visitor data
    if (!isValidVisitorData(user)) {
      pendoLog.warn('Invalid visitor data, skipping Pendo initialization');
      resolve();

      return;
    }

    // Check if already initializing
    if (isInitializing) {
      pendoLog.warn('Pendo initialization already in progress, skipping');
      resolve();

      return;
    }

    // If Pendo is ready, check if we need to reinitialize
    if (window.pendo?.isReady()) {
      // Check if visitor data has changed
      if (!hasVisitorChanged(user)) {
        resolve();

        return;
      }

      // Check rate limiting
      if (!canInitialize()) {
        resolve();

        return;
      }

      pendoLog.info('Pendo is already ready, reinitializing with new user...');
      initializePendo(user);
      resolve();

      return;
    }

    // Check rate limiting for new initialization
    if (!canInitialize()) {
      resolve();

      return;
    }

    isInitializing = true;
    pendoLog.info('Loading Pendo script...');

    const script = document.createElement('script');
    script.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
    script.async = true;

    const cleanup = () => {
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
      window.removeEventListener('unload', cleanup);
      isInitializing = false;
    };

    script.onload = () => {
      pendoLog.success('Pendo script loaded successfully');
      initializePendo(user);
      isInitializing = false;
      resolve();
    };

    script.onerror = () => {
      cleanup();
      pendoLog.error('Failed to load Pendo script');
      reject(new Error('Failed to load the Pendo script'));
    };

    window.addEventListener('unload', cleanup);
    document.body.appendChild(script);
  });
};

const initializePendo = (user: PendoVisitorData): void => {
  try {
    // Final validation before sending
    if (!isValidVisitorData(user)) {
      pendoLog.error('Invalid user data, cannot initialize Pendo');

      return;
    }

    const apiKey = import.meta.env.VITE_PENDO_API_KEY;

    if (!apiKey) {
      pendoLog.warn(
        'VITE_PENDO_API_KEY is not defined. Pendo cannot be initialized.',
      );

      return;
    }

    const environment = import.meta.env.MODE || 'development';

    const visitorData = {
      id: String(user.id),
      rolId: user.rolId!,
      companyId: user.companyId,
      environment,
    };

    pendoLog.info('Initializing Pendo with visitor data:', visitorData);

    window.pendo.initialize({
      apiKey,
      visitor: visitorData,
    });

    // Update spam prevention state
    lastInitializationTime = Date.now();
    lastVisitorHash = createVisitorHash(user);

    // Set up event listeners
    setupPendoEventListeners();

    pendoLog.success('Pendo initialized successfully');
    pendoLog.info('Visitor data sent:', visitorData);

    // Verify connection after a brief delay
    setTimeout(() => {
      checkPendoConnection();
    }, 2000);
  } catch (error) {
    pendoLog.error(
      'Error initializing Pendo',
      error instanceof Error ? error.message : error,
    );
  }
};
